<script setup lang="ts">
import { getUsers } from '@/api/users'
import type { Users, } from '@/types/user-types'
import { ref, onMounted, computed } from 'vue'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import { Icon } from '@iconify/vue'
import { userRoleOptions, UserRole } from '@/constants/user'
import defaultAvatar from '@/assets/images/user_avatar.png'
import Dialog from 'primevue/dialog'
import { useToast } from 'primevue/usetoast'
import AddUserForm from '@/components/features/users/AddUserForm.vue'
import RemoveUserConfirmationModal from '@/components/features/users/RemoveUserConfirmationModal.vue'
import EditUserForm from '@/components/features/users/EditUserForm.vue' // <-- 1. IMPORT EditUserForm
import ConfirmDialog from 'primevue/confirmdialog'
import { removeUser} from '@/api/users'

const users = ref<Users[]>([])
const loading = ref<boolean>(false)
const searchQuery = ref<string>('')
const toast = useToast()
const showAddUserDialog = ref(false)
const showRemoveUserDialog = ref(false)
const showEditUserDialog = ref(false)
const userToEdit = ref<Users | null>(null)
const confirmDialogVisible = ref(false)
const userToRemove = ref<Users | null>(null)

const fetchUsersList = async () => {
  loading.value = true
  try {
    users.value = await getUsers()
  } catch (error) {
    console.error('Failed to fetch users:', error)
    toast.add({ severity: 'error', summary: 'Loading Error', detail: 'Failed to load users.', life: 3000 })
  } finally {
    loading.value = false
  }
}

onMounted(fetchUsersList)

const filterUserList = computed(() => {
  if (!users.value) return []
  const query = searchQuery.value.toLowerCase().trim()
  if (!query) return users.value
  return users.value.filter(
    (user) =>
      (user.firstName && user.firstName.toLowerCase().includes(query)) ||
      (user.lastName && user.lastName.toLowerCase().includes(query)) ||
      (user.email && user.email.toLowerCase().includes(query)) ||
      (getUserRoleLabel(user.userRole).toLowerCase().includes(query))
  )
})

const openAddUserDialog = () => {
  showAddUserDialog.value = true
}

const closeAddUserDialog = () => {
  showAddUserDialog.value = false
}

const handleUserAdded = () => {
  fetchUsersList()
  closeAddUserDialog()
}

const handleUserAddSubmissionFailed = (errorDetail?: string) => {
  toast.add({
    severity: 'error',
    summary: 'Addition Failed',
    detail: errorDetail || 'Could not add user. Please review the form.',
    life: 5000
  })
}

const handleAddDialogCancel = () => {
  closeAddUserDialog()
}

const openEditUserDialog = (user: Users) => {
  userToEdit.value = { ...user }
  showEditUserDialog.value = true
}

const closeEditUserDialog = () => {
  showEditUserDialog.value = false
  userToEdit.value = null
}

const handleUserEdited = () => {
  fetchUsersList()
  closeEditUserDialog()
}

const handleUserEditSubmissionFailed = (errorDetail?: string) => {
  console.error('User edit submission failed:', errorDetail)
  toast.add({
    severity: 'error',
    summary: 'Update Failed',
    detail: errorDetail || 'Could not update user. Please review the form.',
    life: 5000
  })
}

const handleEditDialogCancel = () => {
  closeEditUserDialog()
}

const confirmRemoveUser = (user: Users) => {
  userToRemove.value = user
  showRemoveUserDialog.value = true
}

const getUserRoleLabel = (roleValue: UserRole | number): string => {
  const roleOption = userRoleOptions.find(option => option.value === roleValue)
  return roleOption ? roleOption.label : 'Unknown Role'
}

const handleRemoveDialogCancel = () => {
  showRemoveUserDialog.value = false
  userToRemove.value = null
}

const handleUserSuccessfullyRemoved = async () => {
  showRemoveUserDialog.value = false
  userToRemove.value = null
  await fetchUsersList()
}


</script>

<template>
  <PageHeader
    title="Users"
    :description="`Total: ${users.length}`"
    buttonText="Add User"
    buttonIcon="mdi:account-plus"
    :buttonClick="openAddUserDialog"
    iconPos="right"
  />
  <DataTable
    :value="filterUserList"
    paginator :rows="10"
    tableStyle="min-width: 50rem"
    stripedRows
    paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
    :rowsPerPageOptions="[10, 25]"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} users"
    :loading="loading"
    :pt="{ table: loading && 'min-h-[30rem]' }"
    class="w-full mt-6"
  >
    <template #empty><p class="text-center py-4">No users found.</p></template>
    <template #header>
      <div class="flex items-center justify-start font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" placeholder="Search" class="md:w-1/3" />
      </div>
    </template>

    <Column header="Profile" field="firstName" sortable>
      <template #body="slotProps">
        <div class="flex items-center gap-x-3">
          <img :src="slotProps.data.avatarUrl || defaultAvatar" :alt="slotProps.data.firstName"
               class="w-10 h-10 rounded-full object-cover" />
          <span class="font-medium">{{ `${slotProps.data.firstName} ${slotProps.data.lastName}` }}</span>
        </div>
      </template>
    </Column>

    <Column field="email" header="User Email" sortable></Column>

    <Column field="userRole" header="User Role" sortable>
      <template #body="slotProps"><span class="font-medium">{{ getUserRoleLabel(slotProps.data.userRole) }}</span>
      </template>
    </Column>

    <Column header="Action" style="min-width: 15rem;">
      <template #body="slotProps">
        <div class="flex gap-x-2">
          <Button label="Remove User" outlined severity="danger" size="small"
                  @click="confirmRemoveUser(slotProps.data)" />
          <Button label="Edit User" outlined badge-severity="secondary" size="small"
                  @click="openEditUserDialog(slotProps.data)" />
        </div>
      </template>
    </Column>

    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-xl" />
      </Button>
    </template>
  </DataTable>

  <!-- Add User Dialog -->
  <Dialog
    v-model:visible="showAddUserDialog"
    modal
    :draggable="false"
    header=""
    :style="{ width: 'min(95vw, 60rem)' }"
    :breakpoints="{ '960px': '90vw', '640px': '95vw' }"
    @hide="handleAddDialogCancel"
  :closeIcon="''"
  :dismissableMask="true"
  :pt="{
  root: 'border-round',
  header: 'hidden',
  content: 'p-0',
  }"
  >
  <AddUserForm
    @user-added="handleUserAdded"
    @submission-failed="handleUserAddSubmissionFailed"
    @cancel="handleAddDialogCancel"
  />
  </Dialog>

  <Dialog
    v-model:visible="showEditUserDialog"
    modal
    :draggable="false"
    header=""
  :style="{ width: 'min(95vw, 60rem)' }"
  :breakpoints="{ '960px': '90vw', '640px': '95vw' }"
  @hide="handleEditDialogCancel"
  :closeIcon="''"
  :dismissableMask="true"
  :pt="{
  root: 'border-round',
  header: 'hidden',
  content: 'p-0',
  }"
  >
  <EditUserForm
    v-if="userToEdit"
    :user-data-to-edit="userToEdit"
    @user-edited="handleUserEdited"
    @submission-failed="handleUserEditSubmissionFailed"
    @cancel="handleEditDialogCancel"
  />
  </Dialog>

  <ConfirmDialog
    v-model:visible="confirmDialogVisible"
    :message="`Are you sure you want to remove ${userToRemove?.firstName} ${userToRemove?.lastName}?`"
    header="Confirm Removal"
    icon="pi pi-exclamation-triangle"
    accept-label="Yes, Remove"
    reject-label="Cancel"
    accept-class="p-button-danger"
    @accept="removeUser"
    @reject="() => { confirmDialogVisible = false; userToRemove = null; }"
  />
  <Dialog
    v-model:visible="showRemoveUserDialog"
    modal
    :draggable="false"
    header=""
  :style="{ width: 'min(90vw, 35rem)' }"
  :breakpoints="{ '640px': '90vw' }"
  @hide="handleRemoveDialogCancel"
  :closeIcon="''"
  :dismissableMask="true"
  :pt="{
  root: 'border-round',
  header: 'hidden',
  content: 'p-0',
  }"
  >
  <RemoveUserConfirmationModal
    v-if="userToRemove"
    :userToRemove="userToRemove"
    @user-removed="handleUserSuccessfullyRemoved"
    @cancel="handleRemoveDialogCancel"
  />
  </Dialog>

</template>

<style scoped>
:deep(.p-datatable .p-column-header-content) {
  color: #334155;
}

:deep(.p-dialog-content) {
  padding: 0;
}
</style>